import { useMemo, useState } from 'react';
import RepeatIcon from '@mui/icons-material/Repeat';
import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Stack,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { HostTutorBookingResponse } from '../../models/Tutor';
import { isOnlinePaymentHold, planSeriesAccept } from '../../utils/seriesAcceptPlan';
import { tutorBookingStudentNames } from '../../utils/tutorBookingStudentName';
import HostRecurringPartialAcceptDialog from '../HostRecurringPartialAcceptDialog';
import BookingRejectionDialog from '../BookingRejectionDialog';

type Props = {
  seriesId: string;
  bookings: HostTutorBookingResponse[];
  busy: boolean;
  formatWhen: (startTime: string, endTime: string) => string;
  formatAmount: (amount: number, currencyCode: string) => string;
  onAcceptSeries: (seriesId: string) => Promise<void>;
  onAcceptSelected: (seriesId: string, bookingIds: string[], declineReason: string) => Promise<void>;
  onAcceptOne: (bookingId: string) => Promise<void>;
  onDeclineSeries: (seriesId: string, reason: string) => Promise<void>;
  onOpen: (booking: HostTutorBookingResponse) => void;
  onChanged: () => Promise<void> | void;
  onError: (message: string) => void;
};

function isConflictStatus(error: unknown): boolean {
  const status = (error as { response?: { status?: number } })?.response?.status;
  return status === 409;
}

export default function TutorSeriesRequestCard({
  seriesId,
  bookings,
  busy,
  formatWhen,
  formatAmount,
  onAcceptSeries,
  onAcceptSelected,
  onAcceptOne,
  onDeclineSeries,
  onOpen,
  onChanged,
  onError,
}: Props) {
  const { t } = useTranslation();
  const [unchecked, setUnchecked] = useState<string[]>([]);
  const [conflictIds, setConflictIds] = useState<string[]>([]);
  const [partialReason, setPartialReason] = useState('');
  const [partialOpen, setPartialOpen] = useState(false);
  const [declineOpen, setDeclineOpen] = useState(false);
  const [declineReason, setDeclineReason] = useState('');
  const [working, setWorking] = useState(false);

  const rep = bookings[0];
  const online = bookings.every((booking) => isOnlinePaymentHold(booking.paymentStatus));
  const names = tutorBookingStudentNames({
    studentProfile: rep.studentProfile,
    userName: rep.userName,
  });
  const title = names.primaryName || rep.userName || t('tutorBookingRequests.student', 'Student');
  const conflicts = useMemo(() => new Set(conflictIds), [conflictIds]);
  const selected = bookings.filter(
    (booking) => !unchecked.includes(booking.bookingId) && !conflicts.has(booking.bookingId),
  );
  const allChecked = selected.length === bookings.length && conflictIds.length === 0;
  const selectedTotal = selected.reduce((sum, booking) => sum + booking.totalAmount, 0);
  const locked = busy || working;

  const rememberConflicts = (error: unknown, ids: string[]) => {
    if (!isConflictStatus(error)) return false;
    setConflictIds((current) => Array.from(new Set([...current, ...ids])));
    setUnchecked((current) => Array.from(new Set([...current, ...ids])));
    onError(
      t(
        'roomBookingRequests.acceptConflict',
        'Some sessions overlap another booking.',
      ),
    );
    return true;
  };

  const runAccept = async (reason?: string) => {
    const plan = planSeriesAccept({
      memberIds: bookings.map((booking) => booking.bookingId),
      selectedIds: selected.map((booking) => booking.bookingId),
      conflictIds,
      online,
    });
    if (!plan) return;
    setWorking(true);
    try {
      if (plan.kind === 'series') {
        await onAcceptSeries(seriesId);
      } else if (plan.kind === 'selected-online') {
        await onAcceptSelected(seriesId, plan.selectedIds, reason ?? '');
      } else {
        for (const bookingId of plan.selectedIds) {
          await onAcceptOne(bookingId);
        }
      }
      setPartialOpen(false);
      setPartialReason('');
      await onChanged();
    } catch (error) {
      if (!rememberConflicts(error, selected.map((booking) => booking.bookingId))) {
        onError(t('hostTutorBookings.acceptError', 'Failed to accept tutor booking'));
      }
    } finally {
      setWorking(false);
    }
  };

  const confirmDecline = async () => {
    const reason = declineReason.trim();
    if (!reason) return;
    setWorking(true);
    try {
      await onDeclineSeries(seriesId, reason);
      setDeclineOpen(false);
      setDeclineReason('');
      await onChanged();
    } catch {
      onError(t('hostTutorBookings.declineError', 'Failed to decline tutor booking'));
    } finally {
      setWorking(false);
    }
  };

  return (
    <Card variant="outlined" sx={{ borderRadius: 2, height: '100%' }}>
      <CardContent>
        <Stack spacing={1.5}>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Chip size="small" color="warning" label={t('status.pending', 'Pending')} />
            <Chip
              size="small"
              variant="outlined"
              color="secondary"
              icon={<RepeatIcon />}
              label={t('roomBookingRequests.sessionsCount', '{{count}} sessions', {
                count: bookings.length,
              })}
            />
          </Stack>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              {title}
            </Typography>
            {rep.tutorName ? (
              <Typography variant="body2" color="text.secondary">
                {rep.tutorName}
              </Typography>
            ) : null}
            {rep.lessonOptionName ? (
              <Typography variant="body2" color="text.secondary">
                {rep.lessonOptionName}
              </Typography>
            ) : null}
          </Box>
          <Stack spacing={0.5}>
            {bookings.map((booking) => {
              const conflict = conflicts.has(booking.bookingId);
              const checked = !conflict && !unchecked.includes(booking.bookingId);
              return (
                <Stack key={booking.bookingId} direction="row" spacing={0.5} alignItems="center">
                  <Checkbox
                    size="small"
                    checked={checked}
                    disabled={locked || conflict}
                    onChange={(_, value) => {
                      setUnchecked((current) =>
                        value
                          ? current.filter((id) => id !== booking.bookingId)
                          : Array.from(new Set([...current, booking.bookingId])),
                      );
                    }}
                  />
                  <Button
                    variant="text"
                    color="inherit"
                    onClick={() => onOpen(booking)}
                    sx={{ justifyContent: 'flex-start', textTransform: 'none' }}
                  >
                    {formatWhen(booking.startTime, booking.endTime)}
                  </Button>
                </Stack>
              );
            })}
          </Stack>
          {selected.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              {t('roomBookingRequests.selectAtLeastOne', 'Select at least one session.')}
            </Typography>
          ) : null}
          {online && !allChecked && selected.length > 0 && conflictIds.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              {t(
                'roomBookingRequests.onlineUncheckedWillBeDeclined',
                'Unchecked sessions will be declined.',
              )}
            </Typography>
          ) : null}
          <Typography variant="caption" color="text.secondary">
            {allChecked
              ? t('roomBookingRequests.totalForSessions', 'Total for {{count}} sessions', {
                  count: bookings.length,
                })
              : t('roomBookingRequests.selectedSessionsCaption', '{{selected}} of {{total}} sessions', {
                  selected: selected.length,
                  total: bookings.length,
                })}
          </Typography>
          <Typography variant="subtitle1" fontWeight={800}>
            {formatAmount(selectedTotal, rep.currencyCode)}
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
            <Button
              variant="contained"
              color="success"
              disabled={locked || selected.length === 0}
              onClick={() => {
                const plan = planSeriesAccept({
                  memberIds: bookings.map((booking) => booking.bookingId),
                  selectedIds: selected.map((booking) => booking.bookingId),
                  conflictIds,
                  online,
                });
                if (plan?.kind === 'selected-online') {
                  setPartialReason('');
                  setPartialOpen(true);
                  return;
                }
                void runAccept();
              }}
              startIcon={working ? <CircularProgress size={16} color="inherit" /> : undefined}
            >
              {allChecked
                ? t('roomBookingRequests.acceptAll', 'Accept all')
                : t('roomBookingRequests.acceptSelected', 'Accept selected ({{count}})', {
                    count: selected.length,
                  })}
            </Button>
            <Button
              variant="outlined"
              color="error"
              disabled={locked}
              onClick={() => {
                setDeclineReason('');
                setDeclineOpen(true);
              }}
            >
              {t('roomBookingRequests.declineAll', 'Decline all')}
            </Button>
          </Stack>
        </Stack>
      </CardContent>
      <HostRecurringPartialAcceptDialog
        open={partialOpen}
        charged={selected.map((booking) => ({
          id: booking.bookingId,
          label: formatWhen(booking.startTime, booking.endTime),
        }))}
        declined={bookings
          .filter((booking) => !selected.some((item) => item.bookingId === booking.bookingId))
          .map((booking) => ({
            id: booking.bookingId,
            label: formatWhen(booking.startTime, booking.endTime),
          }))}
        chargedTotalLabel={formatAmount(selectedTotal, rep.currencyCode)}
        reason={partialReason}
        loading={working}
        onReasonChange={setPartialReason}
        onClose={() => {
          if (!working) setPartialOpen(false);
        }}
        onConfirm={() => {
          if (!partialReason.trim()) return;
          void runAccept(partialReason.trim());
        }}
      />
      <BookingRejectionDialog
        open={declineOpen}
        title={t('roomBookingRequests.declineSeriesTitle', 'Decline recurring booking')}
        description={t(
          'roomBookingRequests.declineDescription',
          'Declining closes this request. The person booking will be notified and will see the reason you provide.',
        )}
        reason={declineReason}
        reasonLabel={t('roomBookingRequests.declineReason', 'Reason')}
        reasonPlaceholder={t(
          'roomBookingRequests.declineReasonPlaceholder',
          'Briefly explain why you cannot accept this request',
        )}
        reasonHelperText={t(
          'roomBookingRequests.declineReasonHelper',
          'A reason is required and will be shared with the person booking.',
        )}
        confirmLabel={t('roomBookingRequests.declineAll', 'Decline all')}
        cancelLabel={t('common.cancel', 'Cancel')}
        closeLabel={t('common.close', 'Close')}
        loading={working}
        onReasonChange={setDeclineReason}
        onClose={() => {
          if (!working) setDeclineOpen(false);
        }}
        onConfirm={() => {
          void confirmDecline();
        }}
        confirmColor="error"
        notice={
          <Typography variant="body2">
            {t('bookings.declineSeriesWarning', 'This will affect all sessions in this series.')}
          </Typography>
        }
      />
    </Card>
  );
}
